import { useEffect, useRef, useState } from "react";
import { Link, Outlet, useNavigate, useLocation } from "react-router";
import {
    LayoutDashboard, MessageSquare, FileText, Package, Wrench,
    LogOut, ChevronRight, Bell, Menu, X, Shield,
    ChevronDown, ExternalLink, User, FolderOpen
} from "lucide-react";
import logo from "../../assets/Solacesmartlogo.webp";
import { account } from "../../lib/appwrite";
import type { Models } from "appwrite";

const navItems = [
    { to: "/admin", label: "Dashboard", icon: LayoutDashboard, exact: true },
    { to: "/admin/enquiries", label: "Enquiries", icon: MessageSquare },
    { to: "/admin/blog", label: "Blog", icon: FileText },
    { to: "/admin/products", label: "Products", icon: Package },
    { to: "/admin/services", label: "Services", icon: Wrench },
    { to: "/admin/projects", label: "Projects", icon: FolderOpen },
];

export function AdminLayout() {
    const navigate = useNavigate();
    const location = useLocation();
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [profileOpen, setProfileOpen] = useState(false);
    const [user, setUser] = useState<Models.User<Models.Preferences> | null>(null);
    const [checking, setChecking] = useState(true);
    const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
    const profileRef = useRef<HTMLDivElement>(null);

    // ── Auth guard ────────────────────────────────────────────────────────────
    useEffect(() => {
        account.get()
            .then((u) => {
                setUser(u);
                const saved = u.prefs?.["avatarDataUrl"] as string | undefined;
                if (saved) setAvatarUrl(saved);
                setChecking(false);
            })
            .catch(() => { setChecking(false); navigate("/admin/login"); });
    }, [navigate]);

    // Close sidebar on route change (mobile)
    useEffect(() => { setSidebarOpen(false); }, [location.pathname]);

    // Close profile dropdown on outside click / Escape
    useEffect(() => {
        const onMouse = (e: MouseEvent) => {
            if (profileRef.current && !profileRef.current.contains(e.target as Node)) {
                setProfileOpen(false);
            }
        };
        const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setProfileOpen(false); };
        document.addEventListener("mousedown", onMouse);
        document.addEventListener("keydown", onKey);
        return () => {
            document.removeEventListener("mousedown", onMouse);
            document.removeEventListener("keydown", onKey);
        };
    }, []);

    const handleLogout = async () => {
        setProfileOpen(false);
        try { await account.deleteSession("current"); } catch { /* ignore */ }
        navigate("/admin/login");
    };

    const isActive = (to: string, exact?: boolean) =>
        exact ? location.pathname === to : location.pathname.startsWith(to);

    const currentPage = navItems.find((n) => isActive(n.to, n.exact))?.label ?? "Admin";

    // Build 1-2 letter initials from name or email
    const initials = (user?.name || user?.email || "A")
        .split(/[\s@]/).filter(Boolean).slice(0, 2)
        .map((w) => w[0].toUpperCase()).join("");

    if (checking) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-background">
                <div className="w-6 h-6 border-2 border-muted-foreground/20 border-t-primary rounded-full animate-spin" />
            </div>
        );
    }

    const SidebarContent = () => (
        <>
            {/* Logo */}
            <div className="px-6 py-5 border-b border-border flex items-center justify-between">
                <div>
                    <img src={logo} alt="Solace Smart Solutions" className="h-10 object-contain" />
                </div>
                <button
                    onClick={() => setSidebarOpen(false)}
                    className="md:hidden p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                >
                    <X className="w-4 h-4" />
                </button>
            </div>

            {/* Nav */}
            <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
                {navItems.map((item) => {
                    const active = isActive(item.to, item.exact);
                    return (
                        <Link
                            key={item.to}
                            to={item.to}
                            className={`flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-all ${active
                                ? "bg-primary text-primary-foreground shadow-sm"
                                : "text-muted-foreground hover:text-foreground hover:bg-muted"
                                }`}
                        >
                            <item.icon className={`w-4 h-4 shrink-0 ${active ? "text-primary-foreground" : "text-muted-foreground"}`} />
                            {item.label}
                            {active && <ChevronRight className="w-3 h-3 ml-auto opacity-70" />}
                        </Link>
                    );
                })}
            </nav>

            {/* User + Logout */}
            <div className="p-4 border-t border-border">
                <div className="px-3 py-3 mb-2 rounded-lg bg-muted/50 border border-border/50">
                    <p className="text-[10px] text-muted-foreground font-semibold uppercase tracking-wider">Signed in as</p>
                    <p className="text-sm text-foreground font-medium truncate mt-0.5">{user?.email || "admin"}</p>
                </div>
                <button
                    onClick={handleLogout}
                    className="flex items-center gap-3 w-full px-3 py-2.5 rounded-md text-sm font-medium text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-all"
                >
                    <LogOut className="w-4 h-4 shrink-0" /> Sign Out
                </button>
            </div>
        </>
    );

    return (
        <div className="min-h-screen flex bg-background font-sans text-foreground">
            {/* Mobile overlay */}
            {sidebarOpen && (
                <div
                    className="fixed inset-0 z-40 bg-background/80 backdrop-blur-sm md:hidden"
                    onClick={() => setSidebarOpen(false)}
                />
            )}

            {/* Sidebar */}
            <aside
                className={`fixed inset-y-0 left-0 z-50 w-64 bg-card border-r border-border flex flex-col transition-transform duration-300 ease-in-out
          ${sidebarOpen ? "translate-x-0" : "-translate-x-full"} md:translate-x-0`}
            >
                <SidebarContent />
            </aside>

            {/* Main */}
            <div className="flex-1 md:ml-64 min-h-screen flex flex-col min-w-0 overflow-hidden bg-muted/20">
                {/* Top bar */}
                <header className="bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 border-b border-border flex items-center px-4 md:px-8 py-3 sticky top-0 z-30">
                    <button
                        onClick={() => setSidebarOpen(true)}
                        className="md:hidden p-2 -ml-2 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors mr-3"
                    >
                        <Menu className="w-5 h-5" />
                    </button>
                    <img src={logo} alt="Solace" className="h-6 object-contain md:hidden mr-3" />

                    {/* Breadcrumb */}
                    <div className="hidden md:flex items-center gap-2 text-sm">
                        <span className="text-muted-foreground font-medium">Platform</span>
                        <ChevronRight className="w-4 h-4 text-muted-foreground/50" />
                        <span className="text-foreground font-semibold">{currentPage}</span>
                    </div>
                    <span className="md:hidden text-sm font-semibold text-foreground">{currentPage}</span>

                    {/* Right side */}
                    <div className="ml-auto flex items-center gap-2">
                        {/* ── Profile Dropdown ─────────────────────────── */}
                        <div className="relative" ref={profileRef}>
                            {/* Trigger button */}
                            <button
                                onClick={() => setProfileOpen((p) => !p)}
                                className={`flex items-center gap-2 pl-1.5 pr-2 py-1.5 rounded-md transition-colors ${profileOpen ? "bg-muted" : "hover:bg-muted"}`}
                            >
                                {/* Avatar circle */}
                                <div className="h-7 w-7 rounded-full bg-primary flex items-center justify-center text-primary-foreground text-xs font-bold shrink-0 select-none overflow-hidden">
                                    {avatarUrl ? <img src={avatarUrl} alt="avatar" className="w-full h-full object-cover" /> : initials}
                                </div>
                                {/* Name + email — hidden on small screens */}
                                <div className="hidden sm:flex flex-col text-left leading-tight min-w-0">
                                    <span className="text-xs font-semibold text-foreground leading-tight">{user?.name || "Admin"}</span>
                                    <span className="text-[10px] text-muted-foreground truncate max-w-[110px]">{user?.email}</span>
                                </div>
                                <ChevronDown className={`w-3.5 h-3.5 text-muted-foreground hidden sm:block transition-transform duration-200 ${profileOpen ? "rotate-180" : ""}`} />
                            </button>

                            {/* Dropdown panel */}
                            {profileOpen && (
                                <div className="absolute right-0 top-[calc(100%+8px)] w-72 bg-popover border border-border rounded-lg shadow-lg z-50 overflow-hidden">
                                    {/* ── Header ── */}
                                    <div className="px-4 py-4 bg-muted/40 border-b border-border">
                                        <div className="flex items-center gap-3">
                                            <div className="h-12 w-12 rounded-full bg-primary flex items-center justify-center text-primary-foreground text-base font-bold shrink-0 select-none overflow-hidden">
                                                {avatarUrl ? <img src={avatarUrl} alt="avatar" className="w-full h-full object-cover" /> : initials}
                                            </div>
                                            <div className="min-w-0 flex-1">
                                                <p className="text-sm font-semibold text-foreground truncate">{user?.name || "Admin User"}</p>
                                                <p className="text-xs text-muted-foreground truncate mt-0.5">{user?.email}</p>
                                                <div className="flex items-center gap-1.5 mt-2">
                                                    <Shield className="w-3 h-3 text-primary" />
                                                    <span className="text-[10px] font-semibold text-primary uppercase tracking-wider">Administrator</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* ── Menu items ── */}
                                    <div className="p-1.5 space-y-0.5">
                                        <Link
                                            to="/admin/profile"
                                            onClick={() => setProfileOpen(false)}
                                            className="flex items-center gap-3 px-3 py-2 rounded-md text-sm text-foreground hover:bg-muted transition-colors group"
                                        >
                                            <User className="w-4 h-4 text-muted-foreground group-hover:text-foreground shrink-0" />
                                            Profile Settings
                                        </Link>
                                        <a
                                            href="/"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            onClick={() => setProfileOpen(false)}
                                            className="flex items-center gap-3 px-3 py-2 rounded-md text-sm text-foreground hover:bg-muted transition-colors group"
                                        >
                                            <ExternalLink className="w-4 h-4 text-muted-foreground group-hover:text-foreground shrink-0" />
                                            View Website
                                            <ExternalLink className="w-3 h-3 text-muted-foreground/40 ml-auto" />
                                        </a>
                                    </div>

                                    {/* ── Account info ── */}
                                    <div className="mx-1.5 mb-1.5 px-3 py-2.5 rounded-md bg-muted/50 border border-border/60">
                                        <p className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold mb-1">Account ID</p>
                                        <p className="text-xs font-mono text-foreground/60 truncate">{user?.$id || "—"}</p>
                                    </div>

                                    {/* ── Sign out ── */}
                                    <div className="p-1.5 border-t border-border">
                                        <button
                                            onClick={handleLogout}
                                            className="flex items-center gap-3 w-full px-3 py-2 rounded-md text-sm font-medium text-destructive hover:bg-destructive/10 transition-colors"
                                        >
                                            <LogOut className="w-4 h-4 shrink-0" />
                                            Sign Out
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </header>

                {/* Page content */}
                <main className="flex-1 p-4 md:p-8 overflow-x-hidden overflow-y-auto w-full max-w-[1600px] mx-auto">
                    <Outlet />
                </main>
            </div>
        </div>
    );
}
